import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { getRequest, IntlMessage, AsyncSelect } from 'bluesquare-components';
import { useQueryClient } from 'react-query';
import { openSnackBar } from 'Iaso/components/snackBars/EventDispatcher';
import { errorSnackBar } from 'Iaso/constants/snackBars';
import { useAsyncInitialState } from 'Iaso/hooks/useAsyncInitialState';
import MESSAGES from '../../../constants/messages';
import { Campaign } from '../../../constants/types';
import {
    CampaignCategory,
    CAMPAIGNS_ENDPOINT,
    getURL,
    makeCampaignQueryKey,
    Options,
    useGetCampaigns,
} from '../hooks/api/useGetCampaigns';
import { useCampaignTypeNames } from './useCampaignTypeNames';

type Props = {
    handleChange: (keyValue: string, value: unknown) => void;
    label?: IntlMessage;
    multi?: boolean;
    keyValue?: string;
    clearable?: boolean;
    initialValue?: string; // obr name
    showTest?: boolean;
    campaignCategory?: CampaignCategory;
    campaignType?: 'polio' | 'non-polio' | string;
};

/**
 * A wrapper on AsyncSelect geared towards polio campaigns
 * The filters to apply to the campaigns API can be parametrized using the components props
 * The component will automatically pass fieldset=dropdown to limit the fields returned by the API and reformat
 * the response to be usable by AsyncSelect
 *
 */

export const CampaignAsyncSelect: FunctionComponent<Props> = ({
    keyValue = 'campaigns',
    handleChange,
    multi,
    clearable,
    initialValue,
    campaignCategory = 'regular' as CampaignCategory,
    showTest = false,
    campaignType = 'polio',
    label = MESSAGES.campaign,
}) => {
    const queryClient = useQueryClient();
    const campaignTypes = useCampaignTypeNames(campaignType);
    const [search, setSearch, isStateSet] = useAsyncInitialState<
        string | undefined
    >(initialValue);

    const baseOptions = useMemo(() => {
        return {
            fieldset: 'dropdown',
            campaignCategory,
            show_test: showTest,
        };
    }, [campaignCategory, showTest]);
    const options: Options = useMemo(() => {
        return {
            enabled: isStateSet,
            campaignType: campaignTypes,
            search,
            ...baseOptions,
        };
    }, [campaignTypes, search, isStateSet, baseOptions]);

    const fetchOptions = useCallback(
        async (query: string): Promise<any[]> => {
            const { queryKey, params: campaignOptions } = makeCampaignQueryKey({
                options: {
                    enabled: true, // explicitly passing to get same query key as useGetCampaigns
                    search: query,
                    ...baseOptions,
                    campaignType: campaignTypes,
                },
            });
            // Return cached result if exists
            const cachedResult = queryClient.getQueryData(queryKey);
            if (cachedResult) {
                return cachedResult as any;
            }
            const url = getURL(campaignOptions, CAMPAIGNS_ENDPOINT);

            try {
                const searchResult = await getRequest(url);
                const result = searchResult.map(option => ({
                    label: option.obr_name,
                    value: option.id,
                    campaign_types: option.campaign_types,
                }));
                // Store result in query cache to avoid double fetching
                queryClient.setQueryData(queryKey, result);
                return result;
            } catch (e) {
                openSnackBar(errorSnackBar(undefined, MESSAGES.error, e));
                return [];
            }
        },
        [baseOptions, campaignTypes, queryClient],
    );

    const { data: selectedCampaigns } = useGetCampaigns(
        options,
        CAMPAIGNS_ENDPOINT,
        undefined,
        { keepPreviousData: false },
    );

    const campaignOptions = useMemo(() => {
        return (
            (selectedCampaigns as Campaign[])?.map(selected => ({
                label: selected.obr_name,
                value: selected.id,
                campaign_types: selected.campaign_types,
            })) ?? []
        );
    }, [selectedCampaigns]);

    const handleChangeCampaigns = useCallback(
        (keyValue, newValue) => {
            const val = multi
                ? newValue?.map(r => r.label)?.join(',') // using label i.o value to get the obr name
                : newValue;
            setSearch(val?.label ? val.label : undefined);
            handleChange(keyValue, val ? val : undefined);
        },
        [handleChange, multi, setSearch],
    );

    return (
        <AsyncSelect
            keyValue={keyValue}
            label={label}
            value={campaignOptions ?? ''}
            onChange={handleChangeCampaigns}
            debounceTime={500}
            multi={multi}
            clearable={clearable}
            fetchOptions={input => fetchOptions(input)}
        />
    );
};
